import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Linkedin, Twitter, Facebook, Copy, Check, Sparkles,
  Clock, CheckCircle, Send, SkipForward, RefreshCw, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';

const PLATFORM_ICONS = { linkedin: Linkedin, twitter: Twitter, facebook: Facebook };
const PLATFORM_COLORS = {
  linkedin: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
  twitter: 'text-sky-400 bg-sky-500/10 border-sky-500/30',
  facebook: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/30',
};
const STATUS_CONFIG = {
  pending: { label: 'Pending Review', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  posted: { label: 'Posted', color: 'bg-cyan-500/20 text-cyan-400', icon: Send },
  skipped: { label: 'Skipped', color: 'bg-slate-500/20 text-slate-400', icon: SkipForward },
};

function SocialPostCard({ post, onUpdate }) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);

  const Icon = PLATFORM_ICONS[post.platform] || Twitter;
  const colors = PLATFORM_COLORS[post.platform] || PLATFORM_COLORS.twitter;
  const statusCfg = STATUS_CONFIG[post.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.icon;

  const handleCopy = () => {
    navigator.clipboard.writeText(post.content);
    setCopied(true);
    toast.success(`Copied ${post.platform_label} post to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveEdit = () => {
    onUpdate(post.id, { content: editContent });
    setEditing(false);
  };

  return (
    <Card data-testid={`social-post-${post.id}`} className={`border ${colors.split(' ')[2]} bg-slate-900/80`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-md ${colors.split(' ').slice(0,2).join(' ')}`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium text-white">{post.platform_label}</span>
            <Badge variant="outline" className={`text-[10px] ${statusCfg.color}`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusCfg.label}
            </Badge>
          </div>
          <span className="text-[10px] text-slate-600">{post.char_count}/{post.max_chars}</span>
        </div>

        {/* Blog source */}
        <p className="text-[10px] text-slate-500 mb-2 truncate">
          From: {post.blog_post_title}
        </p>

        {/* Content */}
        {editing ? (
          <div className="space-y-2">
            <Textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-300 text-xs min-h-[120px]"
              maxLength={post.max_chars}
            />
            <div className="flex gap-2">
              <Button size="sm" className="text-xs bg-green-600 hover:bg-green-700" onClick={handleSaveEdit}>Save</Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => { setEditing(false); setEditContent(post.content); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div
            className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap mb-3 p-3 rounded bg-slate-950/50 border border-slate-800/50 cursor-pointer hover:border-slate-700 transition-colors max-h-48 overflow-y-auto"
            onClick={() => setEditing(true)}
            title="Click to edit"
          >
            {post.content}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" className="text-xs gap-1 border-slate-700" onClick={handleCopy}>
            {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied!' : 'Copy'}
          </Button>

          {post.status === 'pending' && (
            <>
              <Button size="sm" className="text-xs gap-1 bg-green-600 hover:bg-green-700" onClick={() => onUpdate(post.id, { status: 'approved' })}>
                <CheckCircle className="w-3 h-3" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="text-xs gap-1 border-slate-700 text-slate-400" onClick={() => onUpdate(post.id, { status: 'skipped' })}>
                <SkipForward className="w-3 h-3" /> Skip
              </Button>
            </>
          )}

          {(post.status === 'pending' || post.status === 'approved') && (
            <Button size="sm" className="text-xs gap-1 bg-cyan-600 hover:bg-cyan-700" onClick={() => { handleCopy(); onUpdate(post.id, { status: 'posted' }); }}>
              <Send className="w-3 h-3" /> Copy & Mark Posted
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SocialMedia({ apiUrl }) {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState(null);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPlatform) params.platform = filterPlatform;
      const res = await axios.get(`${apiUrl}/social/queue`, { params });
      setPosts(res.data.posts);
      setStats(res.data.stats);
    } catch (err) {
      console.error('Failed to load social queue:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, filterStatus, filterPlatform]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${apiUrl}/social/generate?count=2`);
      toast.success('Generating social posts in background. Refresh in ~2 minutes.');
      setTimeout(fetchQueue, 120000);
    } catch { toast.error('Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleUpdate = async (postId, data) => {
    try {
      await axios.patch(`${apiUrl}/social/posts/${postId}`, data);
      toast.success('Post updated');
      fetchQueue();
    } catch { toast.error('Update failed'); }
  };

  const pendingByPlatform = {};
  posts.filter(p => p.status === 'pending').forEach(p => {
    pendingByPlatform[p.platform] = (pendingByPlatform[p.platform] || 0) + 1;
  });

  return (
    <div data-testid="social-media-page" className="min-h-screen bg-slate-950 p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <div className="p-2 bg-violet-500/10 rounded-lg">
                <Send className="w-6 h-6 text-violet-400" />
              </div>
              Social Media Center
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Review, edit, and post AI-generated content across platforms
            </p>
          </div>
          <Button
            data-testid="generate-social-btn"
            onClick={handleGenerate}
            disabled={generating}
            className="gap-2 bg-violet-600 hover:bg-violet-700"
          >
            <Sparkles className="w-4 h-4" />
            {generating ? 'Generating...' : 'Generate Posts'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="bg-amber-500/5 border-amber-500/20">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-400">{stats.pending || 0}</p>
              <p className="text-[10px] text-slate-500 uppercase">Pending Review</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/5 border-green-500/20">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-400">{stats.approved || 0}</p>
              <p className="text-[10px] text-slate-500 uppercase">Approved</p>
            </CardContent>
          </Card>
          <Card className="bg-cyan-500/5 border-cyan-500/20">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-cyan-400">{stats.posted || 0}</p>
              <p className="text-[10px] text-slate-500 uppercase">Posted</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-5">
          <div className="flex items-center gap-1 mr-2">
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs text-slate-500">Filter:</span>
          </div>
          {[null, 'pending', 'approved', 'posted', 'skipped'].map(s => (
            <Button
              key={s || 'all'}
              size="sm"
              variant={filterStatus === s ? 'default' : 'outline'}
              className="text-xs"
              onClick={() => setFilterStatus(s)}
            >
              {s || 'All'}
            </Button>
          ))}
          <Separator orientation="vertical" className="h-6 bg-slate-800 mx-1" />
          {['linkedin', 'twitter', 'facebook'].map(p => {
            const PIcon = PLATFORM_ICONS[p];
            return (
              <Button
                key={p}
                size="sm"
                variant={filterPlatform === p ? 'default' : 'outline'}
                className="text-xs gap-1"
                onClick={() => setFilterPlatform(filterPlatform === p ? null : p)}
              >
                <PIcon className="w-3 h-3" /> {p}
              </Button>
            );
          })}
          <Button size="sm" variant="ghost" className="text-xs gap-1 text-slate-500" onClick={fetchQueue}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>

        {/* Posts */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <Card key={i} className="bg-slate-900/50 border-slate-800 animate-pulse h-40" />)}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Send className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="mb-2">No posts in queue</p>
            <p className="text-xs">Click "Generate Posts" to create content from your blog articles</p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(post => (
              <SocialPostCard key={post.id} post={post} onUpdate={handleUpdate} />
            ))}
          </div>
        )}

        {/* Daily Workflow */}
        <div className="mt-8 border-t border-slate-800 pt-4 text-xs text-slate-600 space-y-1">
          <p><strong className="text-slate-500">Daily workflow:</strong> KEYS auto-generates posts → You review here → Click "Copy & Mark Posted" → Paste into LinkedIn/Twitter/Facebook</p>
          <p>Posts are generated from your 10 blog articles. New blog posts = new social content automatically.</p>
        </div>
      </div>
    </div>
  );
}
